import styled from 'styled-components';
import {
  CheckBolderIcon,
  Flex, FlexColumn, LockIcon, PageRefName, ScrollSidebarTitleItemProps,
  SidebarMenuItem,
  useIsMsalAuth,
  useIsVisible,
  useRefs,
} from '@equifinance/shared';
import { PropsWithChildren, ReactNode, useState } from 'react';
import { AuthorisationType } from '../../../contexts/brokerages';
import { IconCircleSmall, SectionSidebarItem } from '../../Sidebar/SectionSidebarItem';
import { useCompanyTabNavHeight } from '../../Company/components/CompanyTabNav';
import { useCompanyContext } from '../../../contexts/company';
import {
  BDMContactDetails,
  Label, SidebarInfo as SidebarInfoItem,
} from '../../ManageUser/components/ManageUserSidebar';
import { firmDetailsRefName } from '../sections/FirmDetails';
import { systemSettingsRefName } from '../sections/SystemSettings';
import { brokerageDetailsRefName } from '../sections/FirmDetails/SubSections/BrokerageDetails';
import { representativeRefName } from '../sections/FirmDetails/SubSections/Representative';
import { bankDetailsRefName } from '../sections/FirmDetails/SubSections/BankDetails';
import { SystemSettingsSectionRefName } from '../sections/SystemSettings/SubSections/SystemSettings';
import { notificationsRefName } from '../sections/SystemSettings/SubSections/Notifications';
import calculateOffsetTop from '../../../utils/calculateOffsetTop';
import { principalCompanyDetailsRefName } from '../sections/FirmDetails/SubSections/PrincipalCompanyDetails';
import { CommercialsSectionRefName } from '../sections/Commercials';
import { AnnualReviewRefName } from '../sections/AnnualReview';
import { useBdmAssignedName } from '../hooks/useBdmAssignedName';

const BrokerDetailsSidebarContainer = styled(FlexColumn)` ${({ theme }) => `
  background: ${theme.colours.neutral[9]};
`}`;

const SidebarCollapsibleMenuContainer = styled(FlexColumn)` ${({ theme }) => `
  margin-top:${theme.padding[6]};
`}`;

const SubMenuContainer = styled(FlexColumn)` ${({ theme }) => `
  padding-left: ${theme.padding[2]};
`}`;

const BreakLine = styled.div`${({ theme }) => `
  height: 1px;
  background:${theme.colours.neutral[7]};
  margin-bottom:${theme.padding[4]};
  margin-top:${theme.padding[4]};
`}`;

const SibebarInfo = styled(SidebarInfoItem)` ${({ theme }) => `
  padding: ${theme.padding[2]} ${theme.padding[6]};
`}`;

const MenuContainer = styled(FlexColumn) <{ highlight: boolean }>`${({ theme, highlight }) => `
  gap: ${theme.padding[1]};
  ${highlight ? `border-left: ${theme.borderWidth[3]} solid ${theme.colours.secondary[1]} !important;` : ''}
`}`;

const TitleMenuContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  padding: ${theme.padding[4]} 0 ${theme.padding[4]} ${theme.padding[6]};
  cursor: pointer;
`}`;

const MenuIcon = styled.i` ${({ theme }) => `
  font-size: ${theme.fontSize[3]};
`}`;

const MenuTitle = styled.p` ${({ theme }) => `
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
  font-family: Montserrat;
  color: ${theme.colours.neutral[2]};
`}`;

function SidebarCollapsibleMenu({
  title,
  children,
  refName,
}: PropsWithChildren<{ title: string, refName: string }>) {
  const [open, setOpen] = useState<boolean>(true);
  const { refs } = useRefs();
  const stickyHeaderOffset = useCompanyTabNavHeight();

  const onClickMenuTitle = () => {
    setOpen((prev) => !prev);
  };

  const isOnScreen = useIsVisible(refs[refName], (stickyHeaderOffset - 5));

  return (
    <MenuContainer highlight={isOnScreen}>
      <TitleMenuContainer onClick={onClickMenuTitle}>
        <MenuTitle>{title}</MenuTitle>
        <MenuIcon className={open ? 'mi-chevron-up' : 'mi-chevron-down'} />
      </TitleMenuContainer>

      {open && (<SubMenuContainer>{children}</SubMenuContainer>)}
    </MenuContainer>
  );
}

const SubMenuTitle = styled(Flex)` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  gap: ${theme.padding[1]};
  align-items: center;
`}`;

const MenuItemContainer = styled.div` ${({ theme }) => `
  li {
    border-left: none !important;

    &:active {
      border-left: none !important;
      background: ${theme.colours.secondary[7]} !important;
      border-radius: 5px;
    }

    &:hover {
      background: none;
    }

    > a:active {
      background: ${theme.colours.secondary[7]};

      div {
        font-weight: ${theme.fontWeight.bold};
      }
    }
  }
`}`;

function SubMenuItem({
  children,
  onClick = () => { },
  icon,
}: PropsWithChildren<{
  onClick?: () => void,
  icon?: ReactNode,
}>) {
  return (
    <MenuItemContainer onClick={onClick}>
      <SidebarMenuItem>
        <SubMenuTitle>
          {icon}
          {children}
        </SubMenuTitle>
      </SidebarMenuItem>
    </MenuItemContainer>
  );
}

function ScrollSubMenuItem({
  children,
  refName = '',
  stickyHeaderOffset = 0,
}: ScrollSidebarTitleItemProps) {
  const { refs } = useRefs();
  const { sectionLockedStatus } = useCompanyContext();
  const isLocked = sectionLockedStatus[refName];

  const onClickSidebarItem = (rn: string) => {
    const { current } = refs?.[rn] ?? {};
    const offsetTop = calculateOffsetTop(current);

    if (current) {
      refs[PageRefName]?.current.scrollTo({ top: (offsetTop ?? 0) - (stickyHeaderOffset + 5), behavior: 'smooth' });
    }
  };

  const icon = isLocked ? <LockIcon small /> : (
    <IconCircleSmall>
      <CheckBolderIcon />
    </IconCircleSmall>
  );

  return (
    <SubMenuItem onClick={() => onClickSidebarItem(refName)} icon={icon}>
      {children}
    </SubMenuItem>
  );
}

export default function BrokerDetailsSidebar() {
  const stickyHeaderOffset = useCompanyTabNavHeight();
  const { company: { companyDetails, bdmAssigned } } = useCompanyContext();
  const principalCompanyShown = companyDetails.authorisationType === 'APPOINTED_REPRESENTATIVE';
  const isInternalUser = useIsMsalAuth();
  const BdmName = useBdmAssignedName(bdmAssigned);

  return (
    <BrokerDetailsSidebarContainer>
      <SibebarInfo>
        <Label>{companyDetails?.tradingCompanyName}</Label>
      </SibebarInfo>

      <SibebarInfo>
        <Label>Authorisation type:</Label>
        {' '}
        {AuthorisationType[companyDetails?.authorisationType]}
      </SibebarInfo>

      <SibebarInfo>
        <Label>FCA no:</Label>
        {' '}
        {companyDetails?.fcaNumber}
      </SibebarInfo>

      <SibebarInfo>
        <Label>Status:</Label>
        {' '}
        {companyDetails?.status}
      </SibebarInfo>

      {isInternalUser && (
        <SibebarInfo>
          <Label>BDM Assigned:</Label>
          {' '}
          {BdmName}
        </SibebarInfo>
      )}

      <BreakLine />

      <BDMContactDetails />

      <SidebarCollapsibleMenuContainer>
        <SidebarCollapsibleMenu title="Firm details" refName={firmDetailsRefName}>
          <ScrollSubMenuItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={brokerageDetailsRefName}
          >
            Brokerage details
          </ScrollSubMenuItem>

          <ScrollSubMenuItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={representativeRefName}
          >
            Representative
          </ScrollSubMenuItem>

          <ScrollSubMenuItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={bankDetailsRefName}
          >
            Bank details
          </ScrollSubMenuItem>

          {principalCompanyShown && (
            <ScrollSubMenuItem
              stickyHeaderOffset={stickyHeaderOffset}
              refName={principalCompanyDetailsRefName}
            >
              Principal company details
            </ScrollSubMenuItem>
          )}

        </SidebarCollapsibleMenu>

        <SidebarCollapsibleMenu title="System settings" refName={systemSettingsRefName}>
          {isInternalUser && (
            <ScrollSubMenuItem
              stickyHeaderOffset={stickyHeaderOffset}
              refName={SystemSettingsSectionRefName}
            >
              System Settings
            </ScrollSubMenuItem>
          )}

          <ScrollSubMenuItem
            stickyHeaderOffset={stickyHeaderOffset}
            refName={notificationsRefName}
          >
            Notifications
          </ScrollSubMenuItem>
        </SidebarCollapsibleMenu>

        {isInternalUser && (
          <>
            <SectionSidebarItem
              stickyHeaderOffset={stickyHeaderOffset}
              refName={CommercialsSectionRefName}
              isLocked
            >
              Commercials
            </SectionSidebarItem>

            <SectionSidebarItem
              stickyHeaderOffset={stickyHeaderOffset}
              refName={AnnualReviewRefName}
              isLocked
            >
              Annual Review
            </SectionSidebarItem>
          </>
        )}

      </SidebarCollapsibleMenuContainer>

    </BrokerDetailsSidebarContainer>
  );
}
